/**
 * Pure bucketing rules for the "Now & Next" board (DNKADM-0271), split out of
 * `NowNextView.tsx` so the component file only exports components and Fast
 * Refresh keeps working (DNKADM-0278, the DNKADM-0249/0255/0266 precedent).
 * Behaviour is unchanged — this is a pure move.
 */
import type { Reservation } from '@/api/reservations';
import { deriveStatusBucket } from './derive';

/** "Arriving soon" horizon: confirmed bookings starting within this window. */
export const SOON_WINDOW_MINUTES = 60;
/** How long an ended, never-checked-in booking lingers in Arriving (missed check-in). */
const LINGER_MINUTES = 15;
/**
 * The unresolved queue's look-back horizon (DNKADM-0350 U1) — a bounded window,
 * not forever: an unbounded list becomes a graveyard nobody reads, which is the
 * exact failure mode the queue exists to prevent.
 */
export const UNRESOLVED_WINDOW_DAYS = 7;

/** ms → whole minutes (floor toward zero). */
export function minutesUntil(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - now.getTime()) / 60_000);
}

/** True when the reservation is checked in (attendance marked Showed). */
export function isCheckedIn(r: Reservation): boolean {
  return (r.status ?? '').trim().toLowerCase() === 'showed';
}

export interface Buckets {
  arriving: Reservation[];
  needsConfirmation: Reservation[];
  later: Reservation[];
}

/**
 * Bucket the loaded day window for the board (exported for tests). Cancelled
 * never appears; `pending` → Needs confirmation; confirmed & not checked-in →
 * Arriving when in-progress / starting within the soon window / ended less
 * than the linger ago, else Later today; checked-in → Later today (status
 * chip shows "Checked in"). Each bucket sorts by start time.
 */
export function bucketReservations(
  reservations: Reservation[],
  now: Date,
): Buckets {
  const arriving: Reservation[] = [];
  const needsConfirmation: Reservation[] = [];
  const later: Reservation[] = [];
  for (const r of reservations) {
    const bucket = deriveStatusBucket(r.status);
    if (bucket === 'cancelled') continue;
    if (bucket === 'pending') {
      needsConfirmation.push(r);
      continue;
    }
    if (bucket !== 'confirmed') continue; // neutral/unknown → not actionable here
    if (isCheckedIn(r)) {
      later.push(r);
      continue;
    }
    const startsIn = minutesUntil(r.startTime, now);
    const endsIn = minutesUntil(r.endTime, now);
    const inProgress = startsIn !== null && startsIn <= 0 && (endsIn === null || endsIn > 0);
    const soon = startsIn !== null && startsIn > 0 && startsIn <= SOON_WINDOW_MINUTES;
    const lingering =
      endsIn !== null && endsIn <= 0 && endsIn >= -LINGER_MINUTES;
    if (inProgress || soon || lingering) arriving.push(r);
    else if (startsIn !== null && startsIn > 0) later.push(r);
    // A long-ended, never-checked-in booking drops off the board (grid/queue own it).
  }
  const byStart = (a: Reservation, b: Reservation) =>
    (a.startTime ?? '').localeCompare(b.startTime ?? '');
  arriving.sort(byStart);
  needsConfirmation.sort(byStart);
  later.sort(byStart);
  return { arriving, needsConfirmation, later };
}

/**
 * The unresolved queue (DNKADM-0350 U1) — past bookings whose slot ended
 * without ever being checked in or marked no-show: status still CONFIRMED
 * (never `showed`, never a cancelled/no_show/pending bucket), end time
 * already passed, bounded to the last {@link UNRESOLVED_WINDOW_DAYS} days.
 * `candidates` is expected to already be scoped to the CONFIRMED status and
 * the window (the shell's separate 7-day fetch, independent of whatever date
 * the calendar happens to be showing — the whole point is that a forgotten
 * booking stays visible no matter where the operator navigates) but every
 * check here is re-applied defensively, mirroring every other read in this
 * dir. Sorted OLDEST first (ascending by start time) — the ones most likely
 * to be forgotten surface first. Never throws.
 */
export function unresolvedReservations(
  candidates: Reservation[],
  now: Date,
): Reservation[] {
  const windowStartMs = now.getTime() - UNRESOLVED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const out = candidates.filter((r) => {
    if (deriveStatusBucket(r.status) !== 'confirmed' || isCheckedIn(r)) return false;
    const endsIn = minutesUntil(r.endTime, now);
    if (endsIn === null || endsIn > 0) return false; // hasn't ended yet — not unresolved
    const endMs = r.endTime ? Date.parse(r.endTime) : NaN;
    if (Number.isNaN(endMs) || endMs < windowStartMs) return false; // outside the bounded window
    return true;
  });
  const byStart = (a: Reservation, b: Reservation) =>
    (a.startTime ?? '').localeCompare(b.startTime ?? '');
  return out.sort(byStart);
}
